/**
 * `mergeCommands` (W1-10a; DESIGN.md §5.12 import, §5.16 projects and difficulties; ARCHITECTURE.md §9 import flow).
 *
 * Adds lines of another chart (with their notes, tracks, groups and the assets they use) to this document. Placement
 * in time follows `align` (`MergeAlign`; contract since CR-W1-10a-6, default 'auto'):
 *  - by beats: every source beat b lands on b + beatOffset, so every note stays on its beat grid. This is what
 *    'auto' does when both charts have identical BPM lists (beats then mean the same musical position). The two
 *    charts' offsets are NOT applied: when they differ, the merged items play that much earlier or later than in
 *    their own chart, and the transaction notes the difference (`cmd.merge.offsetDiffers`). With different tempos
 *    (only when 'beats' is asked for) the items keep their beats but not their timing (`cmd.merge.tempoDiffers`);
 *  - by music time: the source beat's chart seconds plus the source offset is a time in the song; minus this
 *    chart's offset, plus the seconds of `beatOffset`, it is this chart's seconds, whose exact beat is used when RPE
 *    can store it, else the nearest 1/1920 beat (counted in `snapped`). This is what 'auto' does when the BPM lists
 *    differ (beats of the two charts then mean different things, so only the song time is common); it applies the
 *    offset difference, which is why the same pair of charts lines up differently by beats and by music time.
 * Keys stay strictly ordered (a key whose new beat would not come after its predecessor's is nudged one 1/1920 step
 * after it, counted); holds keep end > t; raw imported triples are dropped when a beat changes; link groups get
 * fresh ids; a parent that is not merged is dropped (noted); groups are matched by name (or created); assets are added
 * under unique paths. Tracks that now start after beat 0 get the neutral Stay key at beat 0 (lead-in invariant), and
 * an editor line whose keys now start before beat 0 is marked as imported (only imported lines may start before
 * beat 0).
 */

import { HOLD } from '../ease/index';
import { createDocContainer } from '../doc/container';
import { Rat, type BigRational } from '../math/rational';
import { Defaults } from '../model/defaults';
import { kindOfLine } from '../model/lineKind';
import { requiresLeadInKey } from '../model/props';
import type { AssetId, ChartDoc, DocIndexes, GroupId, Key, KeySrc, Line, LineId, Note, NoteSrc } from '../model/types';
import { beatToRat, Beats } from '../time/beat';
import type { Beat } from '../time/types';
import { addAssetRecord } from './assets';
import { docRatTempo, nudgeAfter, ratBeatAt, ratSecAt, ratToBeat, type RatTempo } from './retime';
import { createTrack, lineOwner, type KeyDraft, type Tx } from './structureKit';
import type { MergeCommands } from './types';

/** Arguments of `mergeChart` (contract; `align` since CR-W1-10a-6, default 'auto'). */
export type MergeChartArgs = Parameters<MergeCommands['mergeChart']>[1];

/**
 * How merged items are placed in time: 'auto' by beats when both charts have identical BPM lists, otherwise by music
 * time; 'beats': source beat b lands on b + beatOffset whatever the tempos; 'musicTime': through the song time
 * (tempos and offsets of both charts applied), shifted by the seconds of beatOffset.
 */
export type MergeAlign = NonNullable<MergeChartArgs['align']>;

/** The placement choices (for UIs that list them). */
export const MERGE_ALIGNS: readonly MergeAlign[] = ['auto', 'beats', 'musicTime'];

/** Total offset (seconds, exact): META.offset / 1000 + info.yml offset. */
function offsetRat(doc: ChartDoc): BigRational {
  const info = Rat.fromDecimal(String(doc.pkg.infoOffsetSec)) ?? Rat.fromF64(doc.pkg.infoOffsetSec);
  return Rat.add(Rat.of(doc.timing.offsetMs, 1000), info);
}

function sortedBpm(doc: ChartDoc, idx: Pick<DocIndexes, 'bpmSorted'>): Array<{ t: Beat; bpm: number }> {
  return idx.bpmSorted().flatMap((id) => {
    const p = doc.bpm.get(id);
    return p === undefined ? [] : [{ t: p.t, bpm: p.bpm }];
  });
}

/** True when both charts have the same BPM list (beats then mean the same musical position; offsets may differ). */
function sameBpmList(a: ChartDoc, ai: DocIndexes, b: ChartDoc, bi: DocIndexes): boolean {
  const la = sortedBpm(a, ai);
  const lb = sortedBpm(b, bi);
  return la.length === lb.length && la.every((p, i) => Beats.eq(p.t, (lb[i] as { t: Beat }).t) && p.bpm === lb[i]?.bpm);
}

/** Maps source beats to this document's beats, counting rounded and nudged values. */
class MergeTime {
  /** Values rounded to 1/1920 beat (music-time placement only). */
  snapped = 0;
  /** Values placed one 1/1920 step after their predecessor to keep their list strictly ordered. */
  nudged = 0;
  /** Placement by beats (b + beatOffset). */
  readonly byBeats: boolean;
  /** Both charts have identical BPM lists. */
  readonly sameTempo: boolean;
  /** By beats with identical tempos: how much later (ms; negative: earlier) merged items play than in their own chart. */
  readonly offsetShiftMs: number;
  private failed = false;
  private readonly offset: Beat;
  private readonly src: RatTempo | null;
  private readonly dst: RatTempo | null;
  private readonly shift: BigRational;

  constructor(tx: Tx, source: ChartDoc, srcIdx: DocIndexes, beatOffset: Beat, align: MergeAlign) {
    this.offset = beatOffset;
    this.sameTempo = sameBpmList(source, srcIdx, tx.doc, tx.idx);
    this.byBeats = align === 'beats' || (align === 'auto' && this.sameTempo);
    if (this.byBeats) {
      this.src = null;
      this.dst = null;
      this.shift = Rat.ZERO;
      this.offsetShiftMs = this.sameTempo ? Rat.toF64(Rat.sub(offsetRat(tx.doc), offsetRat(source))) * 1000 : 0;
      return;
    }
    this.offsetShiftMs = 0;
    this.src = docRatTempo(source, srcIdx);
    this.dst = docRatTempo(tx.doc, tx.idx);
    this.shift = Rat.add(Rat.sub(offsetRat(source), offsetRat(tx.doc)), ratSecAt(this.dst, beatToRat(beatOffset)));
  }

  get outOfRange(): boolean {
    return this.failed;
  }

  /** New beat of `b` and whether it had to be rounded (not counted). */
  private convert(b: Beat, report: { rounded: number }): { readonly beat: Beat; readonly snapped: boolean } {
    if (this.byBeats || this.src === null || this.dst === null) {
      try {
        return { beat: Beats.add(b, this.offset, report), snapped: false };
      } catch {
        this.failed = true;
        return { beat: b, snapped: false };
      }
    }
    const sec = Rat.add(ratSecAt(this.src, beatToRat(b)), this.shift);
    const r = ratToBeat(ratBeatAt(this.dst, sec));
    if (r === null) {
      this.failed = true;
      return { beat: b, snapped: false };
    }
    return r;
  }

  /** New beat of `b` (rounding counted in `snapped`). */
  map(b: Beat, report: { rounded: number }): Beat {
    const r = this.convert(b, report);
    if (r.snapped) this.snapped++;
    return r.beat;
  }

  /** New beat of `b`, strictly after `after` (else one 1/1920 step after it, counted in `nudged` only). */
  mapAfter(b: Beat, after: Beat | null, report: { rounded: number }): Beat {
    const r = this.convert(b, report);
    if (after !== null && !Beats.lt(after, r.beat)) {
      this.nudged++;
      return nudgeAfter(after);
    }
    if (r.snapped) this.snapped++;
    return r.beat;
  }
}

/** Provenance without the raw triples (they no longer match a moved beat). */
function keySrcMoved(src: KeySrc | undefined): KeySrc | undefined {
  if (src === undefined) return undefined;
  const { rawStart: _a, rawEnd: _b, ...rest } = src;
  return Object.keys(rest).length > 0 ? rest : undefined;
}

function noteSrcMoved(src: NoteSrc | undefined): NoteSrc | undefined {
  if (src === undefined) return undefined;
  const { rawStart: _a, rawEnd: _b, nonHoldEnd: _c, ...rest } = src;
  return Object.keys(rest).length > 0 ? rest : undefined;
}

/** Merges lines of `source` into the document (see the header); returns the new lines and the inexact positions. */
export function mergeChart(tx: Tx, args: MergeChartArgs): { lines: LineId[]; snapped: number } {
  const { source, lines, beatOffset, align = 'auto' } = args;
  if (!MERGE_ALIGNS.includes(align)) tx.fail({ key: 'cmd.merge.align' });
  if (!Beats.isNormal(beatOffset)) tx.fail({ key: 'cmd.badBeat' });
  if (Beats.cmp(beatOffset, Beats.ZERO) < 0) tx.fail({ key: 'cmd.merge.negativeOffset' });
  const src = createDocContainer(source);
  const sdoc = src.doc;
  const sidx = src.idx;

  // Lines to merge, in the source's export order.
  let chosen: LineId[];
  if (lines === 'all') chosen = [...sdoc.lineOrder];
  else {
    const want = new Set<LineId>();
    for (const id of lines) {
      if (!sdoc.lines.has(id)) tx.fail({ key: 'cmd.merge.lineMissing', params: { id } });
      want.add(id);
    }
    chosen = sdoc.lineOrder.filter((id) => want.has(id));
  }
  if (chosen.length === 0) tx.fail({ key: 'cmd.merge.noLines' });

  const time = new MergeTime(tx, sdoc, sidx, beatOffset, align);

  // Assets used by the merged lines and their notes.
  const neededAssets = new Set<AssetId>();
  const hitsoundAssets = new Set<AssetId>();
  for (const id of chosen) {
    const l = sdoc.lines.get(id) as Line;
    if (l.texture !== null) neededAssets.add(l.texture);
    for (const nid of sidx.notesByLine(id)) {
      const h = sdoc.notes.get(nid)?.hitsound;
      if (h?.kind === 'asset') {
        neededAssets.add(h.asset);
        hitsoundAssets.add(h.asset);
      }
    }
  }
  for (const a of neededAssets) {
    const ref = sdoc.assets.get(a);
    if (ref !== undefined) addAssetRecord(tx, ref, hitsoundAssets.has(a));
    else if (!tx.doc.assets.has(a)) tx.fail({ key: 'cmd.missing.asset', params: { id: a } });
  }

  // Groups, matched by name.
  const groupMap = new Map<GroupId, GroupId>();
  let maxOrder = 0;
  for (const g of tx.doc.groups.values()) maxOrder = Math.max(maxOrder, g.order);
  for (const id of chosen) {
    const gid = sdoc.lines.get(id)?.group ?? null;
    if (gid === null || groupMap.has(gid)) continue;
    const g = sdoc.groups.get(gid);
    if (g === undefined) continue;
    const existing = [...tx.doc.groups.values()].find((x) => x.name === g.name);
    if (existing !== undefined) groupMap.set(gid, existing.id);
    else {
      const nid: GroupId = tx.newId('groups');
      maxOrder++;
      tx.insert('groups', { id: nid, name: g.name, order: maxOrder, color: g.color });
      groupMap.set(gid, nid);
    }
  }

  // Line records (ids first, so parents can be remapped).
  const lineMap = new Map<LineId, LineId>();
  for (const id of chosen) lineMap.set(id, tx.newId('lines'));
  let detached = 0;
  for (const id of chosen) {
    const l = sdoc.lines.get(id) as Line;
    let parent: LineId | null = null;
    if (l.parent !== null) {
      parent = lineMap.get(l.parent) ?? null;
      if (parent === null) detached++;
    }
    const rec: Line = {
      ...l,
      id: lineMap.get(id) as LineId,
      parent,
      group: l.group === null ? null : (groupMap.get(l.group) ?? null),
    };
    tx.insert('lines', rec);
  }
  tx.setOrder('lineOrder', [...tx.doc.lineOrder, ...chosen.map((id) => lineMap.get(id) as LineId)]);

  // Tracks and keys.
  let nextLink = 0;
  for (const k of tx.doc.keys.values()) nextLink = Math.max(nextLink, k.link);
  const linkMap = new Map<number, number>();
  const mapLink = (link: number): number => {
    if (link === 0) return 0;
    let to = linkMap.get(link);
    if (to === undefined) {
      to = ++nextLink;
      linkMap.set(link, to);
    }
    return to;
  };
  const report = tx.rounding;
  let leadIns = 0;
  for (const id of chosen) {
    const l = sdoc.lines.get(id) as Line;
    const to = lineMap.get(id) as LineId;
    const kind = kindOfLine(sdoc, sidx, l);
    let startsBeforeZero = false;
    for (const ids of sidx.tracksOfLine(id).values()) {
      for (const tid of ids) {
        const track = sdoc.tracks.get(tid);
        if (track?.owner.kind !== 'line') continue;
        const { prop, layer } = track.owner;
        const drafts: KeyDraft[] = [];
        let prev: Beat | null = null;
        for (const kid of sidx.keysByTrack(tid)) {
          const k = sdoc.keys.get(kid) as Key;
          const t = time.mapAfter(k.t, prev, report);
          prev = t;
          const s = Beats.eq(t, k.t) ? k.src : keySrcMoved(k.src);
          drafts.push({
            t,
            vIn: k.vIn,
            vOut: k.vOut,
            ease: k.ease,
            link: mapLink(k.link),
            ...(s !== undefined ? { src: s } : {}),
          });
        }
        const first = drafts[0];
        if (first === undefined) continue;
        let imported = track.importedLeadIn === true;
        const c = Beats.cmp(first.t, Beats.ZERO);
        if (imported && c <= 0) imported = false;
        if (requiresLeadInKey(prop) && c > 0 && !imported) {
          const lead = Defaults.leadInValue(prop, layer, kind, l.attachUI !== null) ?? first.vIn;
          drafts[0] = { ...first, vIn: lead };
          drafts.unshift({ t: Beats.ZERO, vIn: lead, vOut: lead, ease: HOLD });
          leadIns++;
        }
        if (requiresLeadInKey(prop) && c < 0) startsBeforeZero = true;
        createTrack(tx, lineOwner(to, prop, layer), track.valueType, drafts, imported);
      }
    }
    if (startsBeforeZero && l.origin === 'editor') tx.update('lines', to, { origin: 'import' });
  }

  // Notes.
  for (const id of chosen) {
    const to = lineMap.get(id) as LineId;
    for (const nid of sidx.notesByLine(id)) {
      const n = sdoc.notes.get(nid) as Note;
      const t = time.map(n.t, report);
      // A hold ends after it starts (an imported zero-length hold stays zero-length); other notes end where they start.
      const end = n.kind === 'hold' && !Beats.eq(n.t, n.end) ? time.mapAfter(n.end, t, report) : t;
      const moved = !Beats.eq(t, n.t) || !Beats.eq(end, n.end);
      const s = moved ? noteSrcMoved(n.src) : n.src;
      const { src: _old, ...rest } = n;
      const rec: Note = {
        ...rest,
        id: tx.newId('notes'),
        line: to,
        t,
        end,
        gen: null,
        ...(s !== undefined ? { src: s } : {}),
      };
      tx.insert('notes', rec);
    }
  }
  if (time.outOfRange) tx.fail({ key: 'cmd.merge.outOfRange' });

  const created = chosen.map((id) => lineMap.get(id) as LineId);
  if (detached > 0) tx.note({ key: 'cmd.merge.parentsDetached', params: { count: detached } });
  if (leadIns > 0) tx.note({ key: 'cmd.merge.leadIns', params: { count: leadIns } });
  // `snapped` counts every position that is not at its exact time (rounded or nudged).
  const snapped = time.snapped + time.nudged;
  if (snapped > 0) tx.note({ key: 'cmd.merge.snapped', params: { count: snapped, nudged: time.nudged } });
  const shiftMs = Math.round(time.offsetShiftMs * 1000) / 1000;
  if (shiftMs !== 0) {
    tx.note({
      key: 'cmd.merge.offsetDiffers',
      params: { ms: Math.abs(shiftMs), direction: shiftMs > 0 ? 'later' : 'earlier' },
    });
  }
  if (time.byBeats && !time.sameTempo) tx.note({ key: 'cmd.merge.tempoDiffers' });
  tx.setSelection({ lines: created, ...(created[0] !== undefined ? { activeLine: created[0] } : {}) });
  tx.setFocus({ beat: beatOffset });
  return { lines: created, snapped };
}

export const mergeCommands: MergeCommands = { mergeChart };
